<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import MediaflowService from '@/services/MediaflowService'
import FormButton from '@/components/Atoms/FormButton.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'

/**
 * Output encoder, edited in place in the Settings panel: resolution tiles,
 * bitrate, frame rate, Save / Cancel. Same PUT /mediaflows/:id payload and
 * store updates as the old modal. Read-only while the production is live.
 */
const props = defineProps({
	mediaflow: { type: Object, required: true },
	/** Plan ceiling: 1080 or 720. */
	mediaflowResolution: { type: Number, default: 720 },
	fpsOptions: { type: Array, default: () => ['25', '30'] },
	/** Saving is blocked while the production is online. */
	locked: { type: Boolean, default: false },
	enabled: { type: Boolean, default: true },
})

const emit = defineEmits(['saved', 'dirty-change'])

const store = useStore()
const { notify } = useNotification()

const RESOLUTIONS = [
	{ label: '1920 x 1080', value: '1080p', orientation: 'Landscape' },
	{ label: '1080 x 1920', value: '1080p', orientation: 'Portrait' },
	{ label: '1280 x 720', value: '720p', orientation: 'Landscape' },
	{ label: '720 x 1280', value: '720p', orientation: 'Portrait' },
]
const BITRATES = ['1000', '2500', '5000']

const resolutionOptions = computed(() => RESOLUTIONS.map((r) => ({
	...r,
	upgrade: props.mediaflowResolution !== 1080 && r.value === '1080p',
})))

const selectedResolution = ref('720p')
const selectedResolutionAlias = ref('1280 x 720')
const bitrate = ref('2500')
const fps = ref('')
const saving = ref(false)
const readOnly = computed(() => props.locked || !props.enabled)

const resolveAlias = (preset, screenResolution) => {
	const aliases = RESOLUTIONS.filter((o) => o.value === preset).map((o) => o.label)
	if (!aliases.length) return ''
	return screenResolution && aliases.includes(screenResolution) ? screenResolution : aliases[0]
}

const stored = computed(() => {
	const mf = props.mediaflow || {}
	const preset = mf.resolutionPreset || '720p'
	const current = (mf.fps || 0).toString()
	const first = props.fpsOptions.length ? props.fpsOptions[0] : '30'
	return {
		preset,
		alias: resolveAlias(preset, mf.screenResolution),
		bitrate: (mf.bitrate || 2500).toString(),
		fps: props.fpsOptions.includes(current) ? current : first,
	}
})
const hydrate = () => {
	selectedResolution.value = stored.value.preset
	selectedResolutionAlias.value = stored.value.alias
	bitrate.value = stored.value.bitrate
	fps.value = stored.value.fps
}
hydrate()
watch(stored, hydrate)

const dirty = computed(() => (
	selectedResolutionAlias.value !== stored.value.alias
	|| bitrate.value !== stored.value.bitrate
	|| fps.value !== stored.value.fps
))
watch(dirty, (v) => emit('dirty-change', v))

const pickResolution = (option) => {
	if (option.upgrade || readOnly.value) return
	selectedResolution.value = option.value
	selectedResolutionAlias.value = option.label
}

const save = async () => {
	if (readOnly.value || saving.value || !dirty.value) return
	saving.value = true
	try {
		const updateData = {
			resolutionPreset: selectedResolution.value,
			screenResolution: selectedResolutionAlias.value,
			bitrate: parseInt(bitrate.value, 10),
			fps: parseInt(fps.value, 10),
		}
		await MediaflowService.updateMediaflows(props.mediaflow._id, updateData)
		const id = props.mediaflow._id
		store.dispatch('Mediaflows/setResolutionPreset', { id, resolutionPreset: updateData.resolutionPreset })
		store.dispatch('Mediaflows/setScreenResolution', { id, screenResolution: updateData.screenResolution })
		store.dispatch('Mediaflows/setBitrate', { id, bitrate: updateData.bitrate })
		store.dispatch('Mediaflows/setFps', { id, fps: updateData.fps })
		notify({ group: 'success', text: 'Output settings saved' })
		emit('saved', updateData)
	} catch (err) {
		console.error('Failed to save stream config:', err)
		notify({ group: 'error', text: err?.message || 'Could not save output settings' })
	} finally {
		saving.value = false
	}
}

const resolutionLabel = (option) => option.label.replace(/\s*x\s*/, ' × ')
defineExpose({ dirty, cancel: hydrate })
</script>

<template>
	<section class="flex flex-col gap-4 rounded-xl bg-surface-1 p-4" data-testid="settings-output">
		<div class="flex items-center justify-between gap-2">
			<h3 class="m-0 text-sm font-semibold text-surface-text-2">Output encoder</h3>
			<span v-if="locked" class="text-xs text-surface-7" data-testid="settings-output-locked">Locked while live</span>
		</div>

		<fieldset class="m-0 border-0 p-0" :disabled="readOnly">
			<legend class="mb-2 text-xs font-medium text-surface-8">Resolution</legend>
			<div class="grid grid-cols-2 gap-2">
				<template v-for="option in resolutionOptions" :key="option.label">
					<Tooltip v-if="option.upgrade" wrap-trigger content="Upgrade to use 1080p" wrapper-class="w-full">
						<button type="button" disabled class="flex w-full flex-col items-start rounded-lg bg-surface-2 px-3 py-2 text-left opacity-50" aria-disabled="true">
							<span class="text-tiny font-medium text-surface-7">{{ resolutionLabel(option) }}</span>
							<span class="text-xs text-surface-6">{{ option.orientation }} · Upgrade</span>
						</button>
					</Tooltip>
					<button
						v-else
						type="button"
						class="flex w-full flex-col items-start rounded-lg px-3 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-not-allowed disabled:opacity-60"
						:class="selectedResolutionAlias === option.label ? 'bg-main-transparent ring-1 ring-main' : 'bg-surface-2 hover:bg-surface-3 disabled:hover:bg-surface-2'"
						:aria-pressed="selectedResolutionAlias === option.label"
						:data-testid="`settings-resolution-${option.label.replace(/\s/g, '')}`"
						@click="pickResolution(option)"
					>
						<span class="text-tiny font-medium text-surface-text-2">{{ resolutionLabel(option) }}</span>
						<span class="text-xs text-surface-8">{{ option.orientation }}</span>
					</button>
				</template>
			</div>
		</fieldset>

		<fieldset class="m-0 border-0 p-0" :disabled="readOnly">
			<legend class="mb-2 text-xs font-medium text-surface-8">Bitrate</legend>
			<TabGroup type="segmented" :cols="3" role="radiogroup" aria-label="Bitrate">
				<TabItem v-for="option in BITRATES" :key="option" :is-active="bitrate === option" role="radio" :aria-checked="bitrate === option" :disabled="readOnly" :data-testid="`settings-bitrate-${option}`" @click="!readOnly && (bitrate = option)">
					{{ option }} kbps
				</TabItem>
			</TabGroup>
		</fieldset>

		<fieldset class="m-0 border-0 p-0" :disabled="readOnly">
			<legend class="mb-2 text-xs font-medium text-surface-8">Frame rate</legend>
			<TabGroup type="segmented" :cols="fpsOptions.length >= 2 && fpsOptions.length <= 5 ? fpsOptions.length : null" role="radiogroup" aria-label="Frame rate">
				<TabItem v-for="option in fpsOptions" :key="option" :is-active="fps === option" role="radio" :aria-checked="fps === option" :disabled="readOnly" @click="!readOnly && (fps = option)">
					{{ option }} fps
				</TabItem>
			</TabGroup>
		</fieldset>

		<div v-if="dirty && !readOnly" class="flex justify-end gap-2" data-testid="settings-output-actions">
			<FormButton type="secondary" size="sm" :disabled="saving" data-testid="settings-output-cancel" @click="hydrate">Cancel</FormButton>
			<FormButton type="main" size="sm" :is-loading="saving" data-testid="settings-output-save" @click="save">Save</FormButton>
		</div>
	</section>
</template>
